import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { persistToken } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Loader2, Eye, EyeOff, User, Mail, Lock, Key } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantJoin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ invite_code: '', name: '', email: '', password: '' });
  const update = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/accountant/team/join`, form);
      if (res.data?.token) persistToken(res.data.token);
      toast.success('Joined practice successfully!');
      navigate('/accountant/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Shield className="h-12 w-12 mx-auto mb-3 text-sky-400" />
          <h1 className="text-2xl font-bold text-white">Join a Practice</h1>
          <p className="text-slate-400 mt-1 text-sm">Enter your invite code to join an existing practice</p>
        </div>
        <Card className="bg-slate-800/90 border-slate-700 shadow-2xl">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Invite Code</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input placeholder="Paste your invite code" value={form.invite_code} onChange={update('invite_code')}
                    className="pl-10 bg-slate-900/80 border-slate-600 text-white h-11" required data-testid="join-invite-code" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Your Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input placeholder="Jane Doe" value={form.name} onChange={update('name')}
                    className="pl-10 bg-slate-900/80 border-slate-600 text-white h-11" required data-testid="join-name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input type="email" placeholder="jane@practice.com" value={form.email} onChange={update('email')}
                    className="pl-10 bg-slate-900/80 border-slate-600 text-white h-11" required data-testid="join-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input type={showPassword ? 'text' : 'password'} placeholder="Create a password" value={form.password} onChange={update('password')}
                    className="pl-10 pr-10 bg-slate-900/80 border-slate-600 text-white h-11" required data-testid="join-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-sky-600 hover:bg-sky-700 h-11" data-testid="join-submit">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Joining...</> : 'Join Practice'}
              </Button>
            </form>
            <p className="text-center text-slate-500 text-xs mt-4">
              <Link to="/accountant/login" className="hover:text-slate-300">Back to login</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccountantJoin;
